import React, { useEffect, useState } from 'react';
import { Card } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from '@/components/ui/dialog';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { StatusBadge } from '@/components/StatusBadge';
import { api } from '@/lib/api';
import { formatINR, formatNumber, formatDateTime } from '@/lib/format';
import { CheckCircle2, XCircle, Eye, Save, PlusCircle } from 'lucide-react';
import { toast } from 'sonner';
import { UserPicker } from '@/components/admin/UserPicker';

const PLATFORMS = ['olaparty', 'trumeet'];

export default function AdminUserStockOrders() {
    const [orders, setOrders] = useState([]);
    const [prices, setPrices] = useState({});
    const [savingPrices, setSavingPrices] = useState(false);
    const [detail, setDetail] = useState(null);
    const [users, setUsers] = useState([]);
    const [alloc, setAlloc] = useState({ user_id: '', platform: 'olaparty', amount: '', note: '' });
    const [allocating, setAllocating] = useState(false);
    const [userRates, setUserRates] = useState({ olaparty: '', trumeet: '' });
    const [savingRates, setSavingRates] = useState(false);
    const selUser = users.find(u => u.id === alloc.user_id);
    useEffect(() => {
        const sp = selUser?.user_stock_prices || {};
        setUserRates({ olaparty: sp.olaparty ?? '', trumeet: sp.trumeet ?? '' });
    }, [alloc.user_id, selUser]);

    const saveUserRates = async () => {
        setSavingRates(true);
        try {
            const { data } = await api.patch(`/admin/users/${alloc.user_id}/stock-prices`, { prices: userRates });
            setUsers(prev => prev.map(u => u.id === alloc.user_id ? { ...u, user_stock_prices: data.user_stock_prices } : u));
            toast.success(`Stock rates saved for ${selUser?.name || selUser?.email}`);
        } catch (e) { toast.error(e.response?.data?.detail || 'Could not save rates'); }
        finally { setSavingRates(false); }
    };
    const backend = process.env.REACT_APP_BACKEND_URL;

    const loadOrders = () => api.get('/admin/user-stock-orders').then(r => setOrders(r.data.orders || [])).catch(() => {});
    const loadPrices = () => api.get('/admin/user-stock-prices').then(r => setPrices(r.data.prices || {})).catch(() => {});
    const loadUsers = () => api.get('/admin/users').then(r => setUsers(r.data.users || [])).catch(() => {});
    useEffect(() => { loadOrders(); loadPrices(); loadUsers(); }, []);

    const savePrices = async () => {
        setSavingPrices(true);
        try {
            await api.put('/admin/user-stock-prices', { prices });
            toast.success('User stock prices saved');
        } catch (e) { toast.error(e.response?.data?.detail || 'Could not save'); }
        finally { setSavingPrices(false); }
    };

    const doAllocate = async () => {
        if (!alloc.user_id) { toast.error('Pick a user'); return; }
        const amt = parseInt(alloc.amount, 10) || 0;
        if (amt <= 0) { toast.error('Enter diamonds to allocate'); return; }
        setAllocating(true);
        try {
            await api.post('/admin/user-allocations', { user_id: alloc.user_id, platform: alloc.platform, amount: amt, note: alloc.note });
            toast.success(`Allocated ${amt.toLocaleString()} 💎`);
            setAlloc(a => ({ ...a, amount: '', note: '' }));
        } catch (e) { toast.error(e.response?.data?.detail || 'Could not allocate'); }
        finally { setAllocating(false); }
    };

    const act = async (o, action) => {
        try {
            await api.post(`/admin/user-stock-orders/${o.id}/${action}`);
            toast.success(action === 'approve' ? `Approved · ${formatNumber(o.diamonds)} 💎 credited` : 'Order rejected');
            setDetail(null); loadOrders();
        } catch (e) { toast.error(e.response?.data?.detail || 'Action failed'); }
    };

    return (
        <div className="space-y-4">
            <Card className="p-4 sm:p-5">
                <div className="font-display font-semibold mb-1">User stock prices (₹ per diamond)</div>
                <p className="text-xs text-muted-foreground mb-3">Rate used to convert a user's bank-transfer amount into stock diamonds.</p>
                <div className="flex flex-wrap items-end gap-4">
                    {PLATFORMS.map(k => (
                        <div key={k} className="space-y-1.5">
                            <Label className="text-xs capitalize">{k}</Label>
                            <Input type="number" step="0.01" min="0" value={prices[k] ?? ''} onChange={e => setPrices(p => ({ ...p, [k]: e.target.value }))} className="w-32" data-testid={`user-stock-price-${k}`} />
                        </div>
                    ))}
                    <Button onClick={savePrices} disabled={savingPrices} className="bg-[#0E2A47] hover:bg-[#0B223A]" data-testid="user-stock-price-save"><Save className="h-4 w-4 mr-1" /> {savingPrices ? 'Saving…' : 'Save prices'}</Button>
                </div>
            </Card>

            <Card className="p-4 sm:p-5" data-testid="admin-manual-allocation-card">
                <div className="flex items-center gap-2 font-display font-semibold"><PlusCircle className="h-4 w-4 text-emerald-600" /> Manual diamond allocation</div>
                <p className="text-xs text-muted-foreground mt-1 mb-3">Directly credit a user's stock balance (no payment).</p>
                <div className="flex flex-wrap items-end gap-3">
                    <div className="space-y-1.5 min-w-[240px]">
                        <Label className="text-xs">User</Label>
                        <UserPicker users={users} value={alloc.user_id} onChange={v => setAlloc(a => ({ ...a, user_id: v }))} testid="manual-alloc-user" />
                    </div>
                    <div className="space-y-1.5">
                        <Label className="text-xs">Platform</Label>
                        <Select value={alloc.platform} onValueChange={v => setAlloc(a => ({ ...a, platform: v }))}>
                            <SelectTrigger className="w-36" data-testid="manual-alloc-platform"><SelectValue /></SelectTrigger>
                            <SelectContent>{PLATFORMS.map(p => <SelectItem key={p} value={p} className="capitalize">{p}</SelectItem>)}</SelectContent>
                        </Select>
                    </div>
                    <div className="space-y-1.5">
                        <Label className="text-xs">Diamonds</Label>
                        <Input type="number" min="1" value={alloc.amount} onChange={e => setAlloc(a => ({ ...a, amount: e.target.value }))} className="w-32" data-testid="manual-alloc-amount" />
                    </div>
                    <div className="space-y-1.5 flex-1 min-w-[160px]">
                        <Label className="text-xs">Note (optional)</Label>
                        <Input value={alloc.note} onChange={e => setAlloc(a => ({ ...a, note: e.target.value }))} data-testid="manual-alloc-note" />
                    </div>
                    <Button onClick={doAllocate} disabled={allocating} className="bg-emerald-600 hover:bg-emerald-700" data-testid="manual-alloc-submit">{allocating ? 'Allocating…' : 'Allocate'}</Button>
                </div>
                {selUser && (
                    <div className="mt-4 rounded-lg border border-dashed border-border p-3" data-testid="manual-alloc-user-rates">
                        <div className="text-sm font-medium">Stock rates for {selUser.name || selUser.email}</div>
                        <p className="text-[11px] text-muted-foreground mb-2">Custom ₹ per diamond for this user's stock top-ups. Leave blank to use the default price above.</p>
                        <div className="flex flex-wrap items-end gap-3">
                            {PLATFORMS.map(k => (
                                <div key={k} className="space-y-1.5">
                                    <Label className="text-xs capitalize">{k}</Label>
                                    <Input type="number" step="0.01" min="0" value={userRates[k]} placeholder={`default ${prices[k] ?? ''}`} onChange={e => setUserRates(r => ({ ...r, [k]: e.target.value }))} className="w-32" data-testid={`manual-alloc-user-rate-${k}`} />
                                </div>
                            ))}
                            <Button variant="outline" onClick={saveUserRates} disabled={savingRates} data-testid="manual-alloc-user-rates-save"><Save className="h-4 w-4 mr-1" /> {savingRates ? 'Saving…' : 'Save user rates'}</Button>
                        </div>
                    </div>
                )}
            </Card>

            <Card className="p-4 sm:p-5">
                <div className="font-display font-semibold mb-3">User stock orders (bank transfer)</div>
                <div className="overflow-x-auto" data-testid="admin-user-stock-orders-table">
                    <Table>
                        <TableHeader><TableRow><TableHead>Date</TableHead><TableHead>User</TableHead><TableHead>Platform</TableHead><TableHead className="text-right">Amount</TableHead><TableHead className="text-right">Diamonds</TableHead><TableHead>Mode / UTR</TableHead><TableHead>Status</TableHead><TableHead className="text-right">Actions</TableHead></TableRow></TableHeader>
                        <TableBody>
                            {orders.map(o => (
                                <TableRow key={o.id} data-testid={`admin-user-stock-order-${o.id}`}>
                                    <TableCell className="whitespace-nowrap">{formatDateTime(o.created_at)}</TableCell>
                                    <TableCell><div className="font-medium">{o.real_name || '—'}</div><div className="text-xs text-muted-foreground">{o.user_email}</div></TableCell>
                                    <TableCell className="capitalize">{o.platform_name || o.platform}</TableCell>
                                    <TableCell className="text-right">{formatINR(o.amount_inr)}</TableCell>
                                    <TableCell className="text-right tabular-nums">{formatNumber(o.diamonds)}</TableCell>
                                    <TableCell className="text-xs"><span className="font-medium">{o.transfer_mode}</span><div className="font-mono text-muted-foreground">{o.utr}</div></TableCell>
                                    <TableCell><StatusBadge status={o.status} /></TableCell>
                                    <TableCell className="text-right">
                                        <div className="flex items-center justify-end gap-1.5">
                                            <Button size="sm" variant="ghost" onClick={() => setDetail(o)} data-testid={`admin-user-stock-view-${o.id}`}><Eye className="h-4 w-4" /></Button>
                                            {o.status === 'pending' && (
                                                <>
                                                    <Button size="sm" variant="outline" className="text-emerald-700 border-emerald-200" onClick={() => act(o, 'approve')} data-testid={`admin-user-stock-approve-${o.id}`}><CheckCircle2 className="h-3.5 w-3.5 mr-1" /> Approve</Button>
                                                    <Button size="sm" variant="outline" className="text-rose-700 border-rose-200" onClick={() => act(o, 'reject')} data-testid={`admin-user-stock-reject-${o.id}`}><XCircle className="h-3.5 w-3.5 mr-1" /> Reject</Button>
                                                </>
                                            )}
                                        </div>
                                    </TableCell>
                                </TableRow>
                            ))}
                            {orders.length === 0 && <TableRow><TableCell colSpan={8} className="text-center text-muted-foreground py-8">No user stock orders.</TableCell></TableRow>}
                        </TableBody>
                    </Table>
                </div>
            </Card>

            <Dialog open={!!detail} onOpenChange={(o) => !o && setDetail(null)}>
                <DialogContent className="max-w-lg max-h-[90vh] overflow-y-auto" data-testid="admin-user-stock-detail">
                    <DialogHeader><DialogTitle>Stock transfer details</DialogTitle><DialogDescription>Review the bank-transfer details and payment screenshot before approving.</DialogDescription></DialogHeader>
                    {detail && (
                        <div className="text-sm space-y-1.5">
                            <div><span className="text-muted-foreground">User:</span> <strong>{detail.real_name}</strong> ({detail.user_email})</div>
                            <div><span className="text-muted-foreground">Platform:</span> {detail.platform_name || detail.platform}</div>
                            <div><span className="text-muted-foreground">Amount:</span> {formatINR(detail.amount_inr)} → <strong>{formatNumber(detail.diamonds)} 💎</strong></div>
                            <div><span className="text-muted-foreground">Mode:</span> {detail.transfer_mode} · <span className="text-muted-foreground">UTR:</span> <span className="font-mono">{detail.utr}</span></div>
                            <div><span className="text-muted-foreground">Account:</span> <span className="font-mono">{detail.account_number}</span></div>
                            <div><span className="text-muted-foreground">Bank:</span> {detail.bank_name || '—'} · <span className="text-muted-foreground">IFSC:</span> <span className="font-mono">{detail.ifsc || '—'}</span></div>
                            <div><span className="text-muted-foreground">When:</span> {detail.transfer_date} {detail.transfer_time}</div>
                            {detail.screenshot_path && (
                                <div className="mt-2 grid grid-cols-2 gap-2">
                                    {(detail.screenshot_paths || [detail.screenshot_path]).map((_, i) => (
                                        <a key={i} href={`${backend}/api/admin/user-stock-orders/${detail.id}/screenshot?i=${i}`} target="_blank" rel="noreferrer">
                                            <img src={`${backend}/api/admin/user-stock-orders/${detail.id}/screenshot?i=${i}`} alt={`payment screenshot ${i + 1}`} className="rounded-lg border border-border max-h-80 object-contain" data-testid={i === 0 ? 'admin-user-stock-screenshot' : `admin-user-stock-screenshot-${i}`} />
                                        </a>
                                    ))}
                                </div>
                            )}
                            {detail.status === 'pending' && (
                                <div className="flex gap-2 pt-3">
                                    <Button size="sm" className="bg-emerald-600 hover:bg-emerald-700" onClick={() => act(detail, 'approve')} data-testid="admin-user-stock-detail-approve"><CheckCircle2 className="h-4 w-4 mr-1" /> Approve & credit</Button>
                                    <Button size="sm" variant="outline" className="text-rose-700 border-rose-200" onClick={() => act(detail, 'reject')} data-testid="admin-user-stock-detail-reject"><XCircle className="h-4 w-4 mr-1" /> Reject</Button>
                                </div>
                            )}
                        </div>
                    )}
                </DialogContent>
            </Dialog>
        </div>
    );
}
